import React, { useState, useEffect } from "react";
import { Link, useLocation, useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HamburgerMenu from "@/components/HamburgerMenu";
import OptImg from "@/components/OptImg";

const GuestHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const { t, i18n } = useTranslation("common");

  // route lang from /:lang/*
  const { lang: routeLang } = useParams();
  const lang = routeLang && ["en", "id"].includes(routeLang) ? routeLang : "en";

  // keep i18n in sync with URL param (no re-route; just for text)
  useEffect(() => {
    if (i18n.language !== lang) i18n.changeLanguage(lang);
  }, [i18n, lang]);

  // Build a URL with current lang
  const withLang = (path) => {
    const clean = path.startsWith("/") ? path : `/${path}`;
    return `/${lang}${clean}`;
  };

  // Replace only the lang segment, keep path/query/hash
  const switchTo = (target) => {
    const pathname = location.pathname;
    const langPattern = /^\/(en|id)(\/|$)/;

    if (langPattern.test(pathname)) {
      return pathname.replace(langPattern, `/${target}$2`);
    }

    return `/${target}${pathname}`;
  };

  // Explicit handler for language switching with fallback
  const handleLanguageSwitch = (targetLang) => (e) => {
    e.preventDefault();

    try {
      const newPath = switchTo(targetLang);
      i18n.changeLanguage(targetLang);
      history.push(newPath);
    } catch (error) {
      console.error("Language switch error:", error);

      const fallbackPath = `/${targetLang}${location.pathname.replace(
        /^\/(en|id)/,
        ""
      )}`;
      i18n.changeLanguage(targetLang);
      history.push(fallbackPath);
    }
  };

  return (
    <header
      className="w-full bg-white pt-8 pb-4 px-6 md:px-12 flex items-center min-h-[72px] relative z-50"
      aria-label={t("a11y.header")}
    >
      {/* Logo (always visible) */}
      <div className="flex-shrink-0">
        <Link to={withLang("/")}>
          <OptImg
            src="https://res.cloudinary.com/dmvui9sur/image/upload/v1753453810/WhatsApp_Image_2025-07-23_at_16.03.06_1_1_uv2osr.png"
            priority
            maxWidth={300}
            alt={t("brand.logoAlt")}
            className="h-8 w-auto"
          />
        </Link>
      </div>

      {/* Desktop nav */}
      <nav
        className="hidden md:flex items-center space-x-10 ml-12 flex-1"
        aria-label={t("a11y.primaryNav")}
      >
        <Link
          to={withLang("/")}
          className="relative group text-xl font-normal text-black px-1"
        >
          {t("nav.home")}
          <span
            className="absolute left-0 -bottom-0.5 w-0 h-[2px] bg-black transition-all duration-300 group-hover:w-full"
            style={{ borderRadius: 2 }}
          />
        </Link>

        <Link
          to={withLang("/about")}
          className="relative group text-xl font-normal text-black px-1"
        >
          {t("nav.about")}
          <span
            className="absolute left-0 -bottom-0.5 w-0 h-[2px] bg-black transition-all duration-300 group-hover:w-full"
            style={{ borderRadius: 2 }}
          />
        </Link>

        {/* Products: click navigates to /product, hover shows dropdown */}
        <div className="relative group">
          <Link
            to={withLang("/product")}
            className="relative text-xl font-normal text-black px-1"
            aria-haspopup="true"
            aria-expanded="false"
          >
            {t("nav.products")}
            <span
              className="absolute left-0 -bottom-0.5 w-0 h-[2px] bg-black transition-all duration-300 group-hover:w-full"
              style={{ borderRadius: 2 }}
            />
          </Link>

          {/* Dropdown */}
          <div className="absolute left-0 top-full min-w-[180px] bg-white shadow-lg rounded-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-20 mt-2 py-2">
            <Link
              to={withLang("/product/arcticstore")}
              className="block px-6 py-2 text-black hover:bg-gray-100 transition"
            >
              {t("product.arcticstore")}
            </Link>
            <Link
              to={withLang("/product/ultrafreezer")}
              className="block px-6 py-2 text-black hover:bg-gray-100 transition"
            >
              {t("product.ultrafreezer")}
            </Link>
            <Link
              to={withLang("/product/arcticblast")}
              className="block px-6 py-2 text-black hover:bg-gray-100 transition"
            >
              {t("product.arcticblast")}
            </Link>
            <Link
              to={withLang("/product/arcticsuperstore")}
              className="block px-6 py-2 text-black hover:bg-gray-100 transition"
            >
              {t("product.arcticsuperstore")}
            </Link>
          </div>
        </div>

        {/* Services: hover shows dropdown, not clickable */}
        <div className="relative group">
          <span
            className="relative text-xl font-normal text-black px-1 cursor-default"
            aria-haspopup="true"
            aria-expanded="false"
          >
            {t("nav.services")}
            <span
              className="absolute left-0 -bottom-0.5 w-0 h-[2px] bg-black transition-all duration-300 group-hover:w-full"
              style={{ borderRadius: 2 }}
            />
          </span>

          {/* Dropdown */}
          <div className="absolute left-0 top-full min-w-[180px] bg-white shadow-lg rounded-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-20 mt-2 py-2">
            <Link
              to={withLang("/service/buy")}
              className="block px-6 py-2 text-black hover:bg-gray-100 transition whitespace-nowrap"
            >
              {t("service.buy")}
            </Link>
            <Link
              to={withLang("/service/rent")}
              className="block px-6 py-2 text-black hover:bg-gray-100 transition whitespace-nowrap"
            >
              {t("service.rent")}
            </Link>
          </div>
        </div>

        <Link
          to={withLang("/blogs")}
          className="relative group text-xl font-normal text-black px-1"
        >
          {t("nav.blogs")}
          <span
            className="absolute left-0 -bottom-0.5 w-0 h-[2px] bg-black transition-all duration-300 group-hover:w-full"
            style={{ borderRadius: 2 }}
          />
        </Link>

        <Link
          to={withLang("/contact-us")}
          className="relative group text-xl font-normal text-black px-1"
        >
          {t("nav.contact")}
          <span
            className="absolute left-0 -bottom-0.5 w-0 h-[2px] bg-black transition-all duration-300 group-hover:w-full"
            style={{ borderRadius: 2 }}
          />
        </Link>
      </nav>

      {/* Right: Lang switcher + Get Started + Hamburger */}
      <div className="flex flex-row items-center space-x-3 ml-auto">
        {/* Language Switcher */}
        <div className="flex items-center">
          {/* Desktop */}
          <div
            className="hidden md:flex items-center border border-black/10 rounded-full overflow-hidden"
            role="group"
            aria-label={t("a11y.langSwitcher")}
          >
            <button
              onClick={handleLanguageSwitch("en")}
              lang="en"
              className={`px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                lang === "en"
                  ? "bg-black text-white"
                  : "text-black hover:bg-gray-50"
              }`}
              aria-current={lang === "en" ? "true" : "false"}
              type="button"
            >
              EN
            </button>
            <button
              onClick={handleLanguageSwitch("id")}
              lang="id"
              className={`px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                lang === "id"
                  ? "bg-black text-white"
                  : "text-black hover:bg-gray-50"
              }`}
              aria-current={lang === "id" ? "true" : "false"}
              type="button"
            >
              ID
            </button>
          </div>

          {/* Mobile — same UI as desktop */}
          <div
            className="flex md:hidden items-center border border-black/10 rounded-full overflow-hidden"
            role="group"
            aria-label={t("a11y.langSwitcher")}
          >
            <button
              onClick={handleLanguageSwitch("en")}
              lang="en"
              className={`px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                lang === "en"
                  ? "bg-black text-white"
                  : "text-black hover:bg-gray-50"
              }`}
              aria-current={lang === "en" ? "true" : "false"}
              type="button"
            >
              EN
            </button>
            <button
              onClick={handleLanguageSwitch("id")}
              lang="id"
              className={`px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                lang === "id"
                  ? "bg-black text-white"
                  : "text-black hover:bg-gray-50"
              }`}
              aria-current={lang === "id" ? "true" : "false"}
              type="button"
            >
              ID
            </button>
          </div>
        </div>

        <Link
          to={withLang("/get-started")}
          className="flex items-center justify-center h-12 px-6 rounded-[14px] bg-[#101616] text-white font-semibold text-lg shadow-[0_4px_24px_0_rgba(16,22,22,0.05)] leading-none"
          aria-label={t("nav.getStarted")}
        >
          {t("nav.getStarted")}
        </Link>

        {/* Hamburger */}
        <button
          className="w-12 h-12 rounded-full bg-black flex items-center justify-center"
          onClick={() => setMenuOpen(true)}
          aria-label={t("a11y.openMenu")}
          aria-haspopup="dialog"
          aria-expanded={menuOpen}
          type="button"
        >
          <span className="block" style={{ lineHeight: 0 }}>
            <svg width={28} height={28} viewBox="0 0 24 24" fill="none">
              <rect x="5" y="7" width="14" height="2" rx="1" fill="#fff" />
              <rect x="5" y="11" width="14" height="2" rx="1" fill="#fff" />
              <rect x="5" y="15" width="14" height="2" rx="1" fill="#fff" />
            </svg>
          </span>
        </button>
      </div>

      {/* Fullscreen mobile menu */}
      <HamburgerMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </header>
  );
};

export default GuestHeader;
